import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, Check, ArrowDown } from "lucide-react";
import { MaskLines, EASE } from "@/components/ui/motion";
import { waLink } from "@/lib/config";

const TRUST = ["Pendekatan personal", "Proses transparan", "Pendampingan terarah"];

const RotatingBadge = () => (
  <motion.div
    animate={{ rotate: 360 }}
    transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
    className="grid h-28 w-28 place-items-center rounded-full bg-white/90 backdrop-blur-xl sm:h-32 sm:w-32"
    aria-hidden="true"
  >
    <svg viewBox="0 0 100 100" className="h-full w-full">
      <defs><path id="c" d="M50,50 m-37,0 a37,37 0 1,1 74,0 a37,37 0 1,1 -74,0" /></defs>
      <text fontSize="8.6" fontWeight="700" fill="#334E68">
        <textPath href="#c" textLength="228" lengthAdjust="spacing">KONSULTASI AWAL GRATIS • MIKA SOLUTION •</textPath>
      </text>
    </svg>
  </motion.div>
);

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1400px]" data-testid="hero-visual">
      <motion.div
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 32px 32px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 32px 32px)" }}
        transition={{ duration: 1.4, ease: EASE, delay: 0.35 }}
        className="relative mx-auto aspect-[4/5] w-full max-w-[520px] overflow-hidden rounded-t-full rounded-b-[32px] bg-ice"
      >
        <motion.img
          src="/images/hero.jpg"
          alt="Perempuan sedang menelaah catatan keuangan dengan tenang di meja kerja"
          style={{ y: imgY, scale: 1.18 }}
          className="h-full w-full object-cover"
          fetchpriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#334E68]/35 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: cardY }}
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, ease: EASE, delay: 1.1 }}
        className="absolute -left-2 bottom-10 w-[230px] rounded-3xl border border-white/60 bg-white/85 p-5 shadow-[0_30px_60px_-30px_rgba(51,78,104,.45)] backdrop-blur-xl sm:-left-10 sm:w-[260px]"
      >
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-accentb">Alur pendampingan</p>
        {["Ceritakan kondisi", "Evaluasi opsi", "Langkah berikutnya"].map((s, i) => (
          <div key={s} className="mt-3 flex items-center gap-3">
            <span className={`grid h-7 w-7 place-items-center rounded-full text-[11px] font-bold ${i === 0 ? "bg-sky-brand text-ink" : "border border-line text-slateb"}`}>0{i + 1}</span>
            <span className="text-[14px] font-semibold text-slateb">{s}</span>
          </div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 1.3 }}
        className="absolute -right-1 top-8 sm:right-0"
      >
        <RotatingBadge />
      </motion.div>
    </div>
  );
};

export const Hero = () => (
  <section className="relative overflow-hidden" data-testid="hero-section">
    <div className="absolute inset-x-0 top-0 h-[85%] bg-gradient-to-b from-ice via-ice/60 to-white" aria-hidden="true" />
    <div className="grid-lines absolute inset-0 opacity-40 [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_70%)]" aria-hidden="true" />
    <div className="wrap relative grid grid-cols-1 items-center gap-14 pb-20 pt-10 sm:pt-16 lg:grid-cols-12 lg:gap-10 lg:pb-28">
      <div className="lg:col-span-7">
        <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }} className="eyebrow !leading-relaxed" data-testid="hero-eyebrow">
          Konsultasi &amp; Pendampingan Penyelesaian Kewajiban Finansial
        </motion.span>
        <MaskLines
          delay={0.15}
          className="mt-7 font-display text-[38px] font-extrabold leading-[0.98] tracking-[-0.035em] sm:text-6xl lg:text-[68px] xl:text-[80px]"
          lines={[
            "Tagihan Menumpuk?",
            <>Saatnya <span className="serif-it font-normal text-accentb">Memahami</span></>,
            "Pilihan",
            "Penyelesaiannya.",
          ]}
        />
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.75 }} className="mt-8 max-w-xl text-base leading-relaxed text-ink md:text-lg" data-testid="hero-subheadline">
          Mulai dari pinjaman online, kartu kredit, hingga KTA, Mika Solution membantu Anda memahami kondisi kewajiban finansial dan mengevaluasi langkah penyelesaian yang lebih terarah.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.9 }} className="mt-10 flex flex-wrap gap-3">
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn btn-sky group !px-7 !py-4" data-testid="hero-cta-primary">
            Konsultasi Sekarang <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
          <Link to="/#layanan" className="btn btn-ghost group !px-7 !py-4" data-testid="hero-cta-secondary">
            Pelajari Layanan <ArrowDown size={17} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </Link>
        </motion.div>
        <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }} className="mt-12 flex flex-wrap gap-x-7 gap-y-3" data-testid="hero-trust">
          {TRUST.map((t) => (
            <li key={t} className="flex items-center gap-2 text-[14px] font-semibold text-slateb">
              <span className="grid h-5 w-5 place-items-center rounded-full bg-sky-brand/25 text-accentb"><Check size={12} strokeWidth={3} /></span>{t}
            </li>
          ))}
        </motion.ul>
      </div>
      <div className="lg:col-span-5"><HeroVisual /></div>
    </div>
  </section>
);
